import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { useRMDispatch } from 'redux/hooks';
import {
  useSelectPreviewFilters,
  useSelectArchivingResults,
} from 'redux/hooks/archiving';
import { useSelectArchivingBackgroundJob } from 'redux/hooks/backgroundJobs';
import {
  previewArchivingThunk,
  fetchUndismissedArchivingJobResultsThunk,
} from 'redux/thunks/archiving';
import {
  PreviewFiltersDTO,
  EPreviewFilterType,
  EPreviewFilterField,
  ArchivingJobExperimentStatusDto,
} from 'business/models/Models';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  setIsPreviewFiltered,
  setIsFilterChangedAfterPreview,
  setPreviewLoading,
  removePreviewRowsByTreeNodeId,
} from 'redux/reducers/archivingReducer';
import { normalizeJobDataObj } from 'redux/utils/archivingProgressUtils';
import {
  isArchivingJobActive,
  isArchivingJobPaused,
  isArchivingJobWaitingForStorage,
} from './hooks/useArchivingProgressHub';
import { hasActiveArchivingJobInResults } from './utils';
import {
  ARCHIVING_FILTER_APPLIED_MESSAGE,
  ARCHIVING_PATH_SELECTED,
  ARCHIVING_STORAGE_ERROR_DESCRIPTION,
  ARCHIVING_STORAGE_ERROR_TITLE,
  AUTO_REFRESH_PREVIEW_DEBOUNCE_MS,
} from './constants';

/**
 * Hook that encapsulates preview dispatch logic. Returns a stable callback that
 * accepts selectedNodes (array of ids) and performs the preview thunk dispatch.
 */
export const usePreviewArchiving = () => {
  const dispatch = useRMDispatch();
  const previewFilters = useSelectPreviewFilters() || {
    filterType: EPreviewFilterType.OlderThan,
    filterField: EPreviewFilterField.None,
    olderThanValue: undefined,
    olderThanUnit: undefined,
    startDate: undefined,
    endDate: undefined,
    isPreviewFiltered: false,
  };

  const onPreviewClick = useCallback(
    async (
      selectedNodes: number[],
      noFilters: boolean = false,
      isRefresh: boolean = false,
      successMessage: string = ARCHIVING_FILTER_APPLIED_MESSAGE,
    ) => {
      // mark preview as filtered in Redux (an unfiltered/reset preview is not "filtered")
      dispatch(setIsPreviewFiltered(!noFilters));
      dispatch(setIsFilterChangedAfterPreview(false));

      const filters: PreviewFiltersDTO = new PreviewFiltersDTO();
      filters.selectedTreeNodeIds = selectedNodes;
      if (noFilters) {
        filters.type = EPreviewFilterType.None;
        filters.filterByField = EPreviewFilterField.None;
      } else {
        filters.type = previewFilters.filterType;
        filters.filterByField = previewFilters.filterField;
        filters.olderThanValue =
          previewFilters.filterType === EPreviewFilterType.OlderThan
            ? previewFilters.olderThanValue
            : undefined;
        // olderThanUnit may be null in state
        filters.olderThanUnit = previewFilters.olderThanUnit ?? undefined;
        filters.startDate = previewFilters.startDate
          ? new Date(previewFilters.startDate)
          : undefined;
        filters.endDate = previewFilters.endDate
          ? new Date(previewFilters.endDate)
          : undefined;
      }

      await dispatch(previewArchivingThunk(filters));

      // Refresh silently re-syncs the current view - only user-initiated
      // filter changes should surface the confirmation snackbar.
      if (!isRefresh) {
        showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, successMessage);
      }
    },
    [dispatch, previewFilters],
  );

  return { onPreviewClick };
};

const getSelectionKey = (selectedNodes: number[]) =>
  [...selectedNodes].sort((left, right) => left - right).join(',');

export const useAutoRefreshPreviewOnTreeSelection = (
  selectedNodes: number[],
  onPreviewClick: (
    selectedNodes: number[],
    noFilters?: boolean,
    isRefresh?: boolean,
    successMessage?: string,
  ) => Promise<void>,
) => {
  const dispatch = useRMDispatch();
  const previewFilters = useSelectPreviewFilters();
  const isJobProcessing = useIsArchivingJobProcessing();
  const previousSelectionRef = useRef<string | null>(null);

  const onPreviewClickRef = useRef(onPreviewClick);
  onPreviewClickRef.current = onPreviewClick;

  const debouncedPreviewClick = useRef(
    debounce((nodes: number[], noFilters: boolean) => {
      void onPreviewClickRef.current(nodes, noFilters, false, ARCHIVING_PATH_SELECTED);
    }, AUTO_REFRESH_PREVIEW_DEBOUNCE_MS),
  ).current;

  useEffect(() => debouncedPreviewClick.cancel, [debouncedPreviewClick]);

  // useLayoutEffect (not useEffect) so the loading flag is set before the browser
  // paints - otherwise the commit where hasSelection flips true but data/isLoading
  // are still stale from the previous selection gets painted for a frame first,
  // producing a visible flicker of the old rows.
  useLayoutEffect(() => {
    if (isJobProcessing) {
      return;
    }

    const selectionKey = getSelectionKey(selectedNodes);
    if (previousSelectionRef.current === null) {
      previousSelectionRef.current = selectionKey;
      return;
    }

    if (previousSelectionRef.current === selectionKey) {
      return;
    }

    previousSelectionRef.current = selectionKey;

    // Unselecting every path should return Preview to the empty prompt, not
    // re-fetch with an empty node list (which would keep the "no experiments"
    // message on screen).
    if (selectedNodes.length === 0) {
      debouncedPreviewClick.cancel();
      return;
    }

    // Mark the preview as loading immediately (ahead of the debounced fetch) so the
    // previously selected node's rows don't flash on screen while the new selection
    // - which may turn out to have no experiments - is still being fetched.
    dispatch(setPreviewLoading());

    const noFilters = previewFilters?.filterField === EPreviewFilterField.None;
    debouncedPreviewClick(selectedNodes, noFilters);
  }, [
    debouncedPreviewClick,
    dispatch,
    isJobProcessing,
    previewFilters?.filterField,
    selectedNodes,
  ]);
};

// Keeps the Preview list in sync when an experiment it's showing is deleted (moved to
// trash) from another tab: Explorer's delete already broadcasts the deleted tree node ids
// on the Explorer channel, so this just prunes any matching rows straight out of Redux -
// no re-fetch, so it doesn't disturb the user's current selection/filters or flicker the
// table.
export const useRemoveDeletedExperimentsFromPreview = () => {
  const dispatch = useRMDispatch();
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);

  useEffect(() => {
    if (!explorerBroadcastChannel) {
      return;
    }

    const handleBroadcastMessage = (messageEvent: MessageEvent) => {
      if (messageEvent.data?.action !== CHANNEL_ACTIONS.EXPLORER_DELETE_NODE) {
        return;
      }
      const { deletedNodeIds } = (messageEvent.data?.payload ?? {}) as {
        deletedNodeIds?: number[];
      };
      if (!deletedNodeIds?.length) {
        return;
      }
      dispatch(removePreviewRowsByTreeNodeId(deletedNodeIds));
    };

    explorerBroadcastChannel.onmessage = handleBroadcastMessage;
    return () => {
      explorerBroadcastChannel.onmessage = null;
    };
  }, [explorerBroadcastChannel, dispatch]);
};

/**
 * Compute whether filters are enabled and whether Preview button should be disabled.
 * Accepts the currently selected node ids (or length) so the hook can be reused
 * across components that have different selection contexts.
 */
export const usePreviewFilterState = (selectedNodes: number[] | number) => {
  const previewFilters = useSelectPreviewFilters() || {
    filterType: EPreviewFilterType.OlderThan,
    filterField: EPreviewFilterField.None,
    olderThanValue: undefined,
    olderThanUnit: undefined,
    startDate: undefined,
    endDate: undefined,
    isPreviewFiltered: false,
  };
  const {
    filterType,
    filterField,
    olderThanValue,
    startDate,
    endDate,
    isPreviewFiltered,
  } = previewFilters;

  const selectionCount = Array.isArray(selectedNodes)
    ? selectedNodes.length
    : selectedNodes;

  const filtersEnabled = filterField !== EPreviewFilterField.None && selectionCount > 0;

  const isPreviewDisabled =
    !filtersEnabled ||
    (filterType === EPreviewFilterType.OlderThan && !olderThanValue) ||
    (filterType === EPreviewFilterType.DateRange && (!startDate || !endDate));

  const isResetDisabled =
    selectionCount === 0 ||
    (filterField === EPreviewFilterField.None && !isPreviewFiltered);

  return { filtersEnabled, isPreviewDisabled, isResetDisabled };
};

export const useIsArchivingJobActive = () => {
  const archivingJob = useSelectArchivingBackgroundJob();
  const archivingResults = useSelectArchivingResults();
  return (
    isArchivingJobActive(archivingJob) || hasActiveArchivingJobInResults(archivingResults)
  );
};

export const useIsArchivingJobWaitingForStorage = () => {
  const archivingJob = useSelectArchivingBackgroundJob();
  return isArchivingJobWaitingForStorage(archivingJob);
};

export const useIsArchivingJobPaused = () => {
  const archivingJob = useSelectArchivingBackgroundJob();
  return isArchivingJobPaused(archivingJob);
};

export const useIsArchivingJobProcessing = () => {
  const archivingJob = useSelectArchivingBackgroundJob();
  const archivingResults = useSelectArchivingResults();

  if (
    isArchivingJobWaitingForStorage(archivingJob) ||
    isArchivingJobPaused(archivingJob)
  ) {
    return false;
  }

  return (
    isArchivingJobActive(archivingJob) || hasActiveArchivingJobInResults(archivingResults)
  );
};

export const useFetchActiveArchivingJobResults = (jobId?: string | null) => {
  const dispatch = useRMDispatch();
  const archivingResults = useSelectArchivingResults();

  useEffect(() => {
    // See Result (?jobId=) loads that job on its own; don't race it with all undismissed rows.
    if (jobId || archivingResults.length > 0) {
      return;
    }
    dispatch(fetchUndismissedArchivingJobResultsThunk());
  }, [jobId, archivingResults.length, dispatch]);
};

export const getArchivingJobErrorMessage = (
  job: BackgroundJobDTO | null,
  fallbackMessage: string = ARCHIVING_STORAGE_ERROR_TITLE,
) => {
  const progressData = normalizeJobDataObj(job?.dataObj) ?? {};
  const errorMessage = progressData.errorMessage ?? progressData.ErrorMessage;

  if (typeof errorMessage === 'string' && errorMessage.length > 0) {
    return errorMessage;
  }

  return fallbackMessage;
};

export const getArchivingResultRowStatusLabel = (
  status: ArchivingJobExperimentStatusDto,
  isJobWaitingForStorage: boolean,
) => {
  switch (status) {
    case ArchivingJobExperimentStatusDto.Completed:
      return 'Archived';
    case ArchivingJobExperimentStatusDto.Running:
      return 'Running';
    case ArchivingJobExperimentStatusDto.Failed:
      return 'Failed';
    case ArchivingJobExperimentStatusDto.Pending:
    default:
      return isJobWaitingForStorage ? 'Paused' : 'Scheduled';
  }
};

export const useArchivingJobErrorNotification = () => {
  const archivingJob = useSelectArchivingBackgroundJob();
  const isWaitingForStorage = useIsArchivingJobWaitingForStorage();
  const shownForJobIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!archivingJob || !isWaitingForStorage) {
      return;
    }

    if (shownForJobIdRef.current === archivingJob.jobId) {
      return;
    }

    shownForJobIdRef.current = archivingJob.jobId;

    const message = getArchivingJobErrorMessage(archivingJob);
    showAlertSnackBar(
      EAlertType.WARNING,
      ESnackBarPosition.TOP,
      `${message} ${ARCHIVING_STORAGE_ERROR_DESCRIPTION}`,
    );
  }, [archivingJob, isWaitingForStorage]);
};
